import { html, nothing, PropertyValueMap, TemplateResult, CSSResultGroup } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { DirectiveResult } from 'lit/directive';
import { ClassMapDirective } from 'lit/directives/class-map';
import { ifDefined } from 'lit/directives/if-defined.js';
import { nanoid } from 'nanoid';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksNumericInputChangeEvent } from '../../events/blocks-numeric-input-change';
import type { CommonAriaLiveValue } from '../../util/common-modal';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import '../button/button';
import { BlocksButton } from '../button/button';
import '../field-note/field-note';
import '../icon/icons/add';
import '../icon/icons/minus';
import '../icon/icons/warning-diamond-filled';
import '../icon/icons/warning-triangle-filled';
import '../skeleton/skeleton';
import type { NumericSize, ValidationStatus } from './numeric-input.model';
import styles from './numeric-input.styles';

/**
 * @slot addButtonIcon - The component before content (generally used to display '+' icon)
 * @slot subtractButtonIcon - The component after content (generally used to display '-' icon)
 * @slot error - errorNote slot
 * @slot field-note - fieldNote slot
 * @slot warning-note - warningSlot slot
 * @slot suffix - displaying the suffix text like "ton"
 * @slot after - for field icon
 * @fires bl-numeric-input-change - emitted when numeric input value is changed
 * @csspart base - The component's base wrapper.
 * @csspart label-loader - The component's label-loader.
 * @csspart label-skeleton - The component's label-skeleton.
 * @csspart body-loader-container - The component's body-loader-container.
 * @csspart body-loader - The component's body-loader.
 * @csspart body-skeleton - The component's body-skeleton.
 * @csspart footer-loader - The component's footer-loader.
 * @csspart footer-skeleton - The component's footer-skeleton.
 * @csspart label-wrapper - The component's label wrapper.
 * @csspart part-label - The component's label slot.
 * @csspart part-label-text - The component's label text slot.
 * @csspart part-notation - The component's notation element.
 * @csspart field-note-wrapper - The component's field-note wrapper.
 * @csspart part-field-note - The component's field-note slot.
 * @csspart warning - The component's warning slot.
 * @csspart part-error - The component's error slot.
 * @csspart icon - The component's icon wrapper.
 * @csspart part-suffix - The component's suffix slot.
 * @csspart part-after - The component's after slot.
 * @csspart container - The component's container.
 * @csspart input-wrapper - The component's input wrapper.
 * @csspart input - The component's `input` element.
 * @csspart button-wrapper - The component's button wrapper.
 * @csspart button-left - The component's button-left.
 * @csspart subtract-button - The component's subtract-button.
 * @csspart subtract-button-icon - The component's `<cat-icon-minus>` icon.
 * @csspart separator-wrapper - The component's separator wrapper.
 * @csspart separator - The component's separator element.
 * @csspart button-right - The component's button-right.
 * @csspart add-button - The component's add-button.
 * @csspart add-button-icon - The component's `<cat-icon-add>` icon.
 */

export class BlocksNumericInput extends BlocksElement {
  /**
   * Add a static formAssociated property to custom element class.
   * This tells the browser to treat the element like a form control
   */
  static formAssociated = true;static styles: CSSResultGroup = styles;

  /**
   * The unique id of the field
   */
  @property({ type: String })
  fieldId!: string;

  /**
   * The form field's label
   */
  @property({ type: String })
  label = 'Label';

  /**
   * Aria label attribute
   * - Provides an accessible name for the numeric input
   */
  @property({ type: String })
  arialabel: string | undefined;

  /**
   * Hide label?
   */
  @property({ type: Boolean })
  hideLabel: boolean | undefined;

  /**
   * Size
   */
  @property({ type: String })
  size: NumericSize = 'lg';

  /**
   * Status
   */
  @property({ type: String })
  status: ValidationStatus;

  /**
   * Checkbox name attribute property
   */
  @property({ type: String })
  name: string | undefined;

  /**
   * Value
   */
  @property({ type: Number })
  value: number | null = 0;

  /**
   * Field note text
   */
  @property({ type: String })
  fieldNote: string | undefined;

  /**
   * Errornote text
   */
  @property({ type: String })
  errorNote: string | undefined;

  /**
   * Warningnote text
   */
  @property({ type: String })
  warningNote: string | undefined;

  /**
   * Subtract button text
   */
  @property({ type: String })
  subtractButtonText = 'decrease by';

  /**
   * Add button text
   */
  @property({ type: String })
  addButtonText = 'increase by';

  /**
   * The numeric input's minimum value.
   */
  @property({ type: Number })
  min: number | undefined;

  /**
   * The numeric input's maximum value.
   */
  @property({ type: Number })
  max: number | undefined;

  /**
   * Aria describedby
   * 1) Used to connect the field note in select field to the select menu for accessibility
   */
  @property({ type: String })
  ariaDescribedBy: string | undefined;

  /**
   * Required attribute
   */
  @property({ type: Boolean })
  required = false;

  /**
   * Notation is used for setting required or optional label
   */
  @property({ type: String })
  notation: string | undefined;

  /**
   * Disabled attribute
   */
  @property({ type: Boolean, reflect: true })
  disabled: boolean | undefined;

  /**
   * Readonly attribute
   */
  @property({ type: Boolean })
  readOnly: boolean | undefined;

  /**
   * Placeholder text
   */
  @property({ type: String })
  placeholder: string | undefined;

  /**
   * Loading state
   * 1) Adds a loader if is true
   */
  @property({ type: Boolean })
  isLoading: boolean | undefined;

  /**
   * Indicates that the input should have focus when the page loads.
   */
  @property({ type: Boolean, reflect: true })
  autoFocus: boolean | undefined;

  /**
   * Inremental/Decremental range
   */
  @property({ type: Number })
  step = 1;

  /**
   * Name or Title for the warning icon
   * meant for accessibility purposes only
   */
  @property({ type: String })
  warningIconTitle = 'warning icon';

  /**
   * Name or Title for the error icon
   * meant for accessibility purposes only
   */
  @property({ type: String })
  errorIconTitle = 'error icon';

  /**
   * Aria Unit label attribute
   * - Provides an accessible unit label for the numeric input
   */
  @property({ type: String })
  ariaUnitLabel: string | undefined;

  /**
   * The ARIA live region politeness level of  value
   *
   * @type {string}
   * @default "polite"
   */
  @property({ type: String })
  arialive: CommonAriaLiveValue = 'polite';

  /**
   * state for aria-describedby of Input field
   * @ignore
   */
  @state()
  private description: string | undefined;

  /**
   * state for aria-label of Input field
   */
  @state()
  private combinedAriaLabel: string | undefined;

  /**
   * query the right button(increment button)
   */
  @query('.cat-c-numeric-input__button-right')
  private rightButton!: BlocksButton;

  /**
   * query the left button(decrement button)
   */
  @query('.cat-c-numeric-input__button-left')
  private leftButton!: BlocksButton;

  /**
   * query the numeric input
   */
  @query('.cat-c-numeric-input__input')
  private numericInput!: HTMLInputElement;

  private internals_ = this.attachInternals?.();

  private showValue = false;

  @query('input') private inputEl!: HTMLInputElement;

  protected slots = new SlotController(this, 'addButtonIcon', 'subtractButtonIcon', 'error', 'field-note', 'warning-note', 'suffix', 'after');

  /**
   * Aria describedby
   * 1) Used to connect the Error note in select field to the select menu for accessibility
   */

  private ariaDescribedByForFieldNote: string | undefined;

  /**
   * Aria describedby
   * 1) Used to connect the Error note in select field to the select menu for accessibility
   */

  private ariaDescribedByForErrorNote: string | undefined;

  /**
   * Aria describedby
   * 1) Used to connect the warning note in select field to the select menu for accessibility
   */
  private ariaDescribedByForWarningNote: string | undefined;

  /**
   * Aria describedby
   * 1) Used to connect slot suffix to numeric input for accessibility
   */
  private ariaDescribedByForSlotSuffix: string | undefined;

  private get hasSuffixSlot(): boolean {
    return this.slots.hasSlotted('suffix');
  }

  private get hasFieldNoteSlot(): boolean {
    return this.slots.hasSlotted('field-note');
  }

  private get hasErrorSlot(): boolean {
    return this.slots.hasSlotted('error');
  }

  private get hasWarningSlot(): boolean {
    return this.slots.hasSlotted('warning-note');
  }

  /**
   * 
   * 1) check if min has changed. If yes check if it is lesser than max and lesser than value.
     If condition is satisfied, then check if left button is disabled (left button will be disabled, if the user has 
      decremented the value till min). If disabled, remove the disabled attribute and set status to undefined or default, as min is properly
      provided in range, else set status to 'error' and disable left button.
   * 2) check if max has changed. If yes check if it is greater than min and greater than  value.
     If condition is satisfied, then check if right button is disabled (right button will be disabled, if the user has 
      incremented the value till max). If disabled, remove the disabled attribute and set status to undefined or default, as max is properly
      provided in range, else set status to 'error' and disable right button..
    * 3) check if value has changed. If yes check if it is greater than  min and lesser than max. If condition is satisfied, then check 
      if right button and left button is disabled (right button will be disabled, if the user has incremented the value till max and left 
      button will be disabled, if the user has decremented the value till min). If disabled, then remove the disabled attribute in left/button 
      respectively and set status to undefined or default as value lies properly in range, else set status to 'error' and disable the button correspondingly;
      
   */
  protected updated(changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    /* 1 */
    if (changedProperties.has('min')) {
      this.checkMin();
    }
    /* 2 */
    if (changedProperties.has('max')) {
      this.checkMax();
    }
    /* 3 */
    if (changedProperties.has('value')) {
      if (this.value != null) {
        this.checkValue();
      }
      this.internals_?.setFormValue(String(this.value));
    }
    if (changedProperties.has('autoFocus') && this.autoFocus) {
      this.focus();
    }
  }

  override focus(): void {
    this.inputEl?.focus();
  }

  /**
   * Left button disabled when value is less than min or current value minus step count less than min
   */
  private checkMin() {
    if (this.min !== undefined && this.value !== null && this.min < this.value && this.value - this.step <= this.min) {
      if (this.max !== undefined && this.min < this.max && this.leftButton?.disabled === true) {
        this.leftButton.disabled = false;
      } else {
        this.leftButton.disabled = true;
      }
    }
  }

  /**
   * Right button disabled when value is greater than min or current value plus step count greater than max
   */
  private checkMax() {
    if (this.max !== undefined && this.value !== null && this.max > this.value && this.max > this.value + this.step) {
      if (this.min !== undefined && this.max > this.min && this.rightButton?.disabled === true) {
        this.rightButton.disabled = false;
      } else {
        this.rightButton.disabled = true;
      }
    }
  }

  /**
   * When value chnages disable the left and right button based on current value with max and min
   */
  private checkValue() {
    if (
      this.min !== undefined &&
      this.max !== undefined &&
      this.value !== null &&
      this.value > this.min &&
      this.value < this.max &&
      this.value + this.step <= this.max
    ) {
      if (this.rightButton.disabled === true) {
        this.rightButton.disabled = false;
      }
      if (this.leftButton.disabled === true) {
        this.leftButton.disabled = false;
      }
    } else {
      if (this.max !== undefined && this.value !== null && this.value >= this.max) {
        this.rightButton.disabled = true;
        this.leftButton.disabled = false;
      } else {
        this.rightButton.disabled = false;
      }
      if (this.min !== undefined && this.value !== null && this.value <= this.min) {
        this.leftButton.disabled = true;
        this.rightButton.disabled = false;
      } else {
        this.leftButton.disabled = false;
      }
    }
  }

  /**
   * Increase
   * Increase the value by step count or by 1 if no step count
   * 1) Method increments the value of a numeric type of <input> element by the value of the step attribute
   */
  private onIncrease(e: MouseEvent | KeyboardEvent) {
    if ((e.currentTarget as BlocksButton).disabled) {
      e.stopImmediatePropagation();
      return false;
    }
    this.showValue = true;
    if (this.disabled || this.readOnly) {
      return false;
    }

    /* 1 */
    this.numericInput.stepUp();
    this.value = Number(this.numericInput.value);
    this.emitEvent();
  }

  /**
   * Decrease
   * Decrement the value by step count or by 1
   * 1) Method decrements the value of a numeric type of <input> element by the value of the step attribute
   */
  private onDecrease(e: MouseEvent | KeyboardEvent) {
    if ((e.currentTarget as BlocksButton).disabled) {
      e.stopImmediatePropagation();
      return false;
    }
    this.showValue = true;
    if (this.disabled || this.readOnly) {
      return false;
    }

    /* 1 */
    this.numericInput.stepDown();
    this.value = Number(this.numericInput.value);
    this.emitEvent();
  }

  /**
   * On change
   * 1) Make the value the value within the input
   * Filter the value for non numeric characters
   */
  private onInput(e: Event) {
    const inputValue = (e.target as HTMLInputElement).value?.replace(/\D/g, '');
    if (inputValue !== '') {
      this.value = +inputValue;
      this.checkValue();
    } else {
      this.value = null;
    }
    this.emitEvent();
  }

  /*
   * Handle numeric input keydown
   */
  private handleInputValue(e: KeyboardEvent) {
    const preventDefaultKeys = ['-', '+', 'e', KEYS.DOWN_ARROW, KEYS.UP_ARROW, KEYS.ENTER, KEYS.SPACE];
    const inputValue = (e.target as HTMLInputElement).value?.replace(/\D/g, '');
    if (preventDefaultKeys.includes(e.key)) {
      e.preventDefault();
      if (e.key === KEYS.DOWN_ARROW) {
        this.onDecrease(e);
      } else if (e.key === KEYS.UP_ARROW) {
        this.onIncrease(e);
      } else if ((e.key === KEYS.ENTER || e.key === KEYS.SPACE) && inputValue !== '') {
        this.value = null;
        this.emitEvent();
      }
    }
    this.showValue = false;
  }

  private emitEvent(): void {
    this.dispatch({ eventName: 'bl-numeric-input-change', detailObj: { value: this.value } }) as BlocksNumericInputChangeEvent;
  }
  /**
   * On mount/update
   * 1) Autogenerate ids, for attribute, and aria-describedby ids to make this
   * accessible if these properties aren't provided
   */
  connectedCallback() {
    super.connectedCallback();
    this.fieldId = this.fieldId || nanoid(); /* 1 */
  }

  /** function is to focus input field on press of Enter/Space key */
  private handleKeydown(e: KeyboardEvent) {
    if (e.key === KEYS.ENTER || e.key === KEYS.SPACE) {
      this.numericInput.focus();
    }
  }

  /**
   * For displaying label
   */
  private getLabel() {
    return html` <div part="label-wrapper" class="cat-c-numeric-input__label-wrap">
      <label part="part-label" class="cat-c-numeric-input__label" for="${ifDefined(this.fieldId)}"
        ><span part="part-label-text" class="cat-c-numeric-input__label-text">${this.label}</span>
        <span part="part-notation" class="cat-c-numeric-input__label-notation">${this.notation}</span>
      </label>
    </div>`;
  }

  /**
   * Loading UI
   */
  private renderLoading(componentClassNames: DirectiveResult<typeof ClassMapDirective>) {
    return html`
      <div part="base" class="${componentClassNames}">
        ${(this.size === 'md' || this.size === 'lg') && !this.hideLabel
          ? html`<div part="label-loader" class="cat-c-numeric-input__label">
              <cat-skeleton
                part="label-skeleton"
                exportparts=${this.getExportParts('skeleton-label', 'skeleton')}
                class="class3"
                size="sm"
                width="128px"
              ></cat-skeleton>
            </div> `
          : nothing}
        <div part="body-loader-container" class="cat-c-numeric-input__container">
          <div part="body-loader" class="cat-c-numeric-input__input-loader">
            <cat-skeleton part="body-skeleton" exportparts=${this.getExportParts('skeleton-body', 'skeleton')}> </cat-skeleton>
            <div part="button-wrapper" class="cat-c-numeric-input__button-wrap">
              <cat-button
                part="button-left"
                exportparts=${this.getExportParts('button-left', 'button')}
                class="cat-c-numeric-input__button cat-c-numeric-input__button-left"
                variant="ghost"
                ?hideText=${true}
                ?skipTab=${true}
                size=${this.size === 'lg' || this.size === 'md' ? 'md' : 'sm'}
              >
                ${this.subtractButtonText}
                <slot part="subtract-button" name="subtractButtonIcon" slot="after">
                  <cat-icon-minus part="subtract-button-icon" exportparts=${this.getExportParts('minus', 'icon')}></cat-icon-minus>
                </slot>
              </cat-button>
              <div part="separator-wrapper" class="cat-c-numeric-input__button--seperator-wrap">
                <div part="separator" class="cat-c-numeric-input__button--seperator"></div>
              </div>

              <cat-button
                part="button-right"
                exportparts=${this.getExportParts('button-right', 'button')}
                class="cat-c-numeric-input__button cat-c-numeric-input__button-right"
                variant="ghost"
                ?hideText=${true}
                ?skipTab=${true}
                size=${this.size === 'lg' || this.size === 'md' ? 'md' : 'sm'}
              >
                ${this.addButtonText}
                <slot part="add-button" name="addButtonIcon" slot="after">
                  <cat-icon-add part="add-button-icon" exportparts=${this.getExportParts('add', 'icon')}></cat-icon-add>
                </slot>
              </cat-button>
            </div>
          </div>
        </div>
        ${this.hasFieldNote()
          ? html`<div part="footer-loader" class="cat-c-numeric-input__footer">
              <cat-skeleton
                part="footer-skeleton"
                exportparts=${this.getExportParts('skeleton-footer', 'skeleton')}
                size="sm"
                width="80px"
              ></cat-skeleton>
            </div>`
          : nothing}
      </div>
    `;
  }

  /**
   * @param noteType which can be 'field-note', 'warning-note' or 'error-note'
   * 1) Display field note from slot or from fieldNote property
   * 2) Display warning note from slot or from warningNote property
   * 3) Display error note from slot or from errorNote property
   */
  private getFooterNote(noteType: string) {
    /* 1 */
    if (noteType === 'field-note') {
      return html`
        <cat-field-note
          part="field-note-wrapper"
          exportparts=${this.getExportParts('field-note')}
          ?isDisabled=${this.disabled}
          id=${ifDefined(this.ariaDescribedByForFieldNote)}
          class="cat-c-numeric-input__field-note-wrapper"
        >
          <slot part="part-field-note" name="field-note">${this.fieldNote}</slot>
        </cat-field-note>
      `;
    }
    /* 2 */
    if (noteType === 'warning-note') {
      return html`
        <cat-field-note
          part="field-note-wrapper"
          exportparts=${this.getExportParts('field-note')}
          ?isDisabled=${this.disabled}
          id=${ifDefined(this.ariaDescribedByForWarningNote)}
          class="cat-c-numeric-input__warning-note-wrapper"
          ><slot part="warning-note" name="warning-note">${this.warningNote}</slot></cat-field-note
        >
      `;
    }
    /* 3 */
    if (noteType === 'error-note') {
      return html`
        <cat-field-note
          part="field-note-wrapper"
          exportparts=${this.getExportParts('field-note')}
          id=${ifDefined(this.ariaDescribedByForErrorNote)}
          ?isDisabled=${this.disabled}
          .status=${'error'}
          class="cat-c-numeric-input__error-note-wrapper"
          ><slot part="part-error" name="error">${this.errorNote}</slot></cat-field-note
        >
      `;
    }
  }

  /**
   * 1) Updates ariaDescribedBy id of Fieldnote whenever its added
   * 2) Updates ariaDescribedBy id of errorNote whenever its added
   * 3) Updates ariaDescribedBy id of warningNote whenever its added
   * 4) Updates ariaDescribedBy id of suffix slot whenever its added
   * 5) Combining aria-label and aria unit label if the numeric input field has any unit in slots
   */
  private setAriaDescribedBy() {
    if (this.hasFieldNote() || this.hasSuffixSlot) {
      this.ariaDescribedBy = this.ariaDescribedBy || nanoid();
      let inputFieldAriaDescribedBy = '';

      if (this.fieldNote || this.hasFieldNoteSlot) {
        /* 1 */
        this.ariaDescribedByForFieldNote = this.ariaDescribedBy;
        inputFieldAriaDescribedBy = this.ariaDescribedByForFieldNote;
      }
      if (this.errorNote || this.hasErrorSlot) {
        /* 2 */
        this.ariaDescribedByForErrorNote = `${this.ariaDescribedBy}_error`;
        inputFieldAriaDescribedBy = `${inputFieldAriaDescribedBy} ${this.ariaDescribedByForErrorNote}`;
      }
      if (this.warningNote || this.hasWarningSlot) {
        /* 3 */
        this.ariaDescribedByForWarningNote = `${this.ariaDescribedBy}_warning`;
        inputFieldAriaDescribedBy = `${inputFieldAriaDescribedBy} ${this.ariaDescribedByForWarningNote}`;
      }
      if (this.hasSuffixSlot) {
        /* 4 */
        this.ariaDescribedByForSlotSuffix = `${this.ariaDescribedBy}_suffix`;
        inputFieldAriaDescribedBy = `${inputFieldAriaDescribedBy} ${this.ariaDescribedByForSlotSuffix}`;
      }

      const combinedIds = inputFieldAriaDescribedBy.trim();

      this.description = combinedIds.length > 0 ? combinedIds : undefined;
    }

    if (this.arialabel || this.ariaUnitLabel) {
      /* 5 */
      this.combinedAriaLabel = `${this.arialabel ? this.arialabel : ''} ${this.ariaUnitLabel ? this.ariaUnitLabel : ''}`;
    }
  }

  private hasFieldNote() {
    return this.fieldNote || this.hasFieldNoteSlot || this.errorNote || this.hasErrorSlot || this.warningNote || this.hasWarningSlot;
  }

  /**
   * Display icons and suffic based on type
   * 1) For displaying the suffix text like "ton"
   * 2) Display warning icon if status is warning.
   * 3) Display error icon if status is error
   * 4) Display field icon if after slot presents
   */
  private getIcons(type: string) {
    /* 1 */
    if (type === 'suffix') {
      return html`<div
        part="icon"
        class="cat-c-numeric-input__button-icon cat-c-numeric-input__suffix-slot"
        id=${ifDefined(this.ariaDescribedByForSlotSuffix)}
      >
        <slot part="part-suffix" name="suffix"></slot>
      </div>`;
    }
    /* 2 */
    if (type === 'warning-icon' && this.status === 'warning') {
      return html`<cat-icon-warning-triangle-filled
        part="icon"
        exportparts=${this.getExportParts('warning-triangle-filled', 'icon')}
        color="knockout-brand"
        .iconTitle=${this.warningIconTitle}
        class="cat-c-numeric-input__button-icon"
      ></cat-icon-warning-triangle-filled>`;
    }
    /* 3 */
    if (type === 'error-icon' && this.status === 'error') {
      return html`<cat-icon-warning-diamond-filled
        part="icon"
        exportparts=${this.getExportParts('warning-diamond-filled', 'icon')}
        color="error"
        .iconTitle=${this.errorIconTitle}
        class="cat-c-numeric-input__button-icon"
      ></cat-icon-warning-diamond-filled>`;
    }
    /* 4 */
    if (type === 'field-icon') {
      return html`<div part="icon" class="cat-c-numeric-input__button-icon cat-c-numeric-input__after-slot">
        <slot part="part-after" name="after" class="cat-c-numeric-input__button-icon"></slot>
      </div>`;
    }
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-numeric-input', {
      'cat-c-numeric-input--loader': this.isLoading === true,
      'cat-c-numeric-input--small': this.size === 'sm',
      'cat-c-numeric-input--medium': this.size === 'md',
      'cat-c-numeric-input--large': this.size === 'lg',
      'cat-c-numeric-input--disabled': this.disabled,
      'cat-is-error': this.status === 'error',
      'cat-is-readonly': this.readOnly,
      'cat-c-numeric-input--hidden-label': this.hideLabel,
      'cat-c-numeric-input--has-field-note': this.fieldNote || this.hasFieldNoteSlot,
      'cat-c-numeric-input--has-warning-note': this.warningNote || this.hasWarningSlot,
      'cat-c-numeric-input--has-error-note': this.errorNote || this.hasErrorSlot,
      'cat-c-numeric-input--has-suffix': this.hasSuffixSlot,
      'cat-c-numeric-input--has-after': this.slots.hasSlotted('after')
    });

    if (this.isLoading) {
      return this.renderLoading(componentClassNames);
    }
    this.setAriaDescribedBy();

    return html`<div part="base" class="${componentClassNames}">
      ${this.size === 'md' || this.size === 'lg' ? this.getLabel() : nothing}
      <div part="container" class="cat-c-numeric-input__container" @keydown=${this.handleKeydown}>
        <div part="input-container" class="cat-c-numeric-input__input-container">
          <div part="input-wrapper" class="cat-c-numeric-input__input-wrap">
            <input
              part="input"
              class="cat-c-numeric-input__input"
              type="number"
              size="1"
              min=${ifDefined(this.min)}
              max=${ifDefined(this.max)}
              step=${ifDefined(this.step)}
              id="${ifDefined(this.fieldId)}"
              pattern="[0-9]*"
              name="${ifDefined(this.name)}"
              .value="${`${this.value ?? ''}`}"
              @input=${(e: Event) => this.onInput(e)}
              @keydown=${this.handleInputValue}
              ?autofocus=${this.autoFocus}
              ?readonly=${this.readOnly || this.disabled}
              aria-describedby="${ifDefined(this.description)}"
              aria-required="${ifDefined(!this.notation ? (this.required ? 'true' : undefined) : undefined)}"
              aria-label="${ifDefined(this.combinedAriaLabel)}"
              placeholder="${ifDefined(this.placeholder)}"
              aria-disabled=${this.disabled === true ? 'true' : 'false'}
              tabindex=${this.disabled ? -1 : 0}
            />
            ${this.showValue ? html`<div class="cat-u-is-vishidden" aria-live=${this.arialive}>${this.value}</div>` : nothing}
            <div part="button-wrapper" class="cat-c-numeric-input__button-wrap">
              <!-- suffix text -->
              ${this.getIcons('suffix')}
              <!-- warning icon -->
              ${this.getIcons('warning-icon')}
              <!-- error icon -->
              ${this.getIcons('error-icon')}
              <!-- field icon -->
              ${this.getIcons('field-icon')}
              <cat-button
                part="button-left"
                exportparts=${this.getExportParts('button-left', 'button')}
                class="cat-c-numeric-input__button cat-c-numeric-input__button-left"
                variant="ghost"
                ?hideText=${true}
                @click=${(e: MouseEvent) => this.onDecrease(e)}
                aria-disabled=${this.disabled === true ? 'true' : 'false'}
                ?skipTab=${true}
                size=${this.size === 'lg' || this.size === 'md' ? 'md' : 'sm'}
              >
                ${this.subtractButtonText + ' ' + this.step}
                <slot part="subtract-button" name="subtractButtonIcon" slot="after">
                  <cat-icon-minus part="subtract-button-icon" exportparts=${this.getExportParts('minus', 'icon')}></cat-icon-minus>
                </slot>
              </cat-button>
              <div part="separator-wrapper" class="cat-c-numeric-input__button--seperator-wrap">
                <div part="separator" class="cat-c-numeric-input__button--seperator"></div>
              </div>
              <cat-button
                part="button-right"
                exportparts=${this.getExportParts('button-right', 'button')}
                class="cat-c-numeric-input__button cat-c-numeric-input__button-right"
                variant="ghost"
                ?hideText=${true}
                @click=${(e: MouseEvent) => this.onIncrease(e)}
                aria-disabled=${this.disabled === true ? 'true' : 'false'}
                ?skipTab=${true}
                size=${this.size === 'lg' || this.size === 'md' ? 'md' : 'sm'}
              >
                ${this.addButtonText + ' ' + this.step}
                <slot part="add-button" name="addButtonIcon" slot="after">
                  <cat-icon-add part="add-button-icon" exportparts=${this.getExportParts('add', 'icon')}></cat-icon-add>
                </slot>
              </cat-button>
            </div>
          </div>
        </div>
        <!--- field note -->
        ${this.getFooterNote('field-note')}
        <!--- warning note -->
        ${this.getFooterNote('warning-note')}
        <!--- error note -->
        ${this.getFooterNote('error-note')}
      </div>
    </div>`;
  }
}

if (customElements.get('cat-numeric-input') === undefined) {
  customElements.define('cat-numeric-input', BlocksNumericInput);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-numeric-input': BlocksNumericInput;
  }
}
